import axios from "axios"
import jwt from "jsonwebtoken"
import { useErrorStore } from "store/useErrorStore"

const { setError } = useErrorStore.getState()

const API_URL = window.__ENV__?.FEATURE_X_ENABLED
  ? window.__ENV__?.API_BASE_URL ?? process.env.REACT_APP_API_URL
  : process.env.REACT_APP_API_URL

const axiosApi = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
})

const refresh = async () => {
  const response = await axiosApi.get("/auth/refreshToken", {
    withCredentials: true,
    isRefreshCall: true,
  })
  return response
}

axiosApi.interceptors.request.use(
  config => {
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`
    }
    return config
  },
  error => Promise.reject(error)
)

axiosApi.interceptors.response.use(
  response => response,
  async error => {
    const prevRequest = error.config
    const status = error.response?.status
    if (status === 401 && prevRequest.isRefreshCall) {
      localStorage.removeItem("token")
      window.location.href = "/login"
      return Promise.reject(error)
    }
    if (status === 401 && !prevRequest._retry) {
      prevRequest._retry = true
      try {
        const { data } = await refresh()
        localStorage.setItem("token", data.accessToken)
        prevRequest.headers.Authorization = `Bearer ${data.accessToken}`
        return axiosApi(prevRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }
    if (status === 401) {
      localStorage.removeItem("token")
      window.location.href = "/login"
    }
    if (status === 500) {
      const currentPath = window.location.pathname
      setError(currentPath)
    }
    return Promise.reject(error.response?.data || error)
  }
)

export async function get(url, config = {}) {
  return await axiosApi.get(url, { ...config }).then(response => response.data)
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then(response => response.data)
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then(response => response.data)
}

const axiosNoCookies = axios.create({
  baseURL: API_URL,
})

axiosNoCookies.interceptors.request.use(function (config) {
  const storageUser = localStorage.getItem("token")
  if (storageUser) {
    config.headers.Authorization = `Bearer ${storageUser}`
  }
  return config
})

axiosNoCookies.interceptors.response.use(
  response => response,
  function (error) {
    if (error.response) {
      const { location } = window
      if (error.response.status === 401 && location.pathname !== "/login") {
        localStorage.removeItem("token")
        location.reload()
      }
    }
    return Promise.reject(error)
  }
)

export async function postNoCookies(url, data, config = {}) {
  return axiosNoCookies
    .post(url, { ...data }, { ...config })
    .then(response => response.data)
}
